import React, { useCallback, useMemo, type PropsWithChildren } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import type {
	ContextualFields,
	IssueToCreate,
} from '@atlassian/jira-issue-table-inline-issue-create/src/common/types.tsx';
import InlineCreateForm, {
	type Props as InlineCreateFormProps,
} from '@atlassian/jira-issue-table-inline-issue-create/src/ui/inline-create-form/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import {
	useIsIssueRankDescending,
	useIsTimelineEnabled,
	useJql,
} from '../../../../controllers/features/selectors.tsx';
import { useScrollStateSelector } from '../../../../controllers/scroll-state/index.tsx';
import { useTimelineCustomComponents } from '../../../../controllers/timeline-custom-components/index.tsx';
import { useIssueCreateMutation } from '../../../../services/issue-create-mutation/CreateIssueController.tsx';

export type Props = Omit<InlineCreateFormProps, 'onCreateIssue'> & {
	issuesConnectionId: string;
	siblingIssueId: string;
	rankFieldId: string | null | undefined;
	parentIssueAri?: string;
	location?: string;
	groupContextFieldInput?: GroupContextField;
};

export const IssueCreateCell = ({
	issuesConnectionId,
	siblingIssueId,
	rankFieldId,
	parentIssueAri,
	location,
	groupContextFieldInput,
	...props
}: Props) => {
	const width = useScrollStateSelector((scrollState) => scrollState.width);

	let jql: string | undefined;
	if (expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		jql = useJql();
	}

	const isIssueRankDescending = useIsIssueRankDescending();
	// JSC-169 Set rankFieldId into formContextualFields so correct rank is set for GIC fallback
	const formContextualFields = useMemo(
		() =>
			isIssueRankDescending
				? { rankAfterIssueId: siblingIssueId, rankFieldId }
				: { rankBeforeIssueId: siblingIssueId, rankFieldId },
		[isIssueRankDescending, siblingIssueId, rankFieldId],
	);

	const onCreateIssue = useIssueCreateMutation({
		issueConnectionId: issuesConnectionId,
		jql,
		...(fg('group_by_any_field_sibling_inline_issue_create') && {
			groupContextFieldInput,
		}),
		...(fg('jsc_list_reranking_gate') && { location }),
	});
	const handleIssueCreate = useCallback(
		(issueToCreate: IssueToCreate, contextualFields: ContextualFields) => {
			onCreateIssue(
				issueToCreate,
				{
					...contextualFields,
					...(parentIssueAri && { parentIssueAri }),
				},
				{
					insertAfter: false,
					insertNodeId: siblingIssueId,
				},
			);
		},
		[onCreateIssue, parentIssueAri, siblingIssueId],
	);

	const isTimelineEnabled = useIsTimelineEnabled();

	const form = (
		<InlineCreateForm
			contextualFields={formContextualFields}
			onCreateIssue={handleIssueCreate}
			rankFieldId={rankFieldId}
			location={location}
			{...props}
		/>
	);

	if (isTimelineEnabled || fg('jira_timeline_harmonisation_m1_issue_table_updates')) {
		return (
			<CustomisableIssueCreateCellWrapper width={width}>{form}</CustomisableIssueCreateCellWrapper>
		);
	}

	return (
		<Box as="td" xcss={cellStyles}>
			{/* eslint-disable-next-line jira/react/no-style-attribute */}
			<Box xcss={boxStyles} style={{ width: `${width}px` }}>
				{form}
			</Box>
		</Box>
	);
};

export type IssueCreateCellWrapperProps = PropsWithChildren<{ width: number }>;

export const IssueCreateCellWrapper = ({ children, width }: IssueCreateCellWrapperProps) => (
	<Box as="td" xcss={cellStyles}>
		{/* eslint-disable-next-line jira/react/no-style-attribute */}
		<Box xcss={boxStyles} style={{ width: `${width}px` }}>
			{children}
		</Box>
	</Box>
);

const CustomisableIssueCreateCellWrapper = ({ children, width }: IssueCreateCellWrapperProps) => {
	const timelineCustomComponents = useTimelineCustomComponents();

	const Wrapper = timelineCustomComponents?.IssueCreateCellWrapper ?? IssueCreateCellWrapper;

	return <Wrapper width={width}>{children}</Wrapper>;
};

const cellStyles = xcss({
	height: '40px',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	left: 'space.0',
	position: 'sticky',
	zIndex: 'card',
});

const boxStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	left: 'space.0',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: 'space.0',
});
